"use client";

/**
 * End-of-post "interactive hook" - the bridge from reading to using
 * the product. Blog readers historically bounced because the article
 * ended in a rating widget and nothing else; this block puts LIVE
 * product value right under the text, none of it behind registration:
 *
 *   - district posts (frontmatter `district: <slug>`): the live
 *     TrendChart for that district + a link to its full stats page;
 *   - every post: the current Baku average AZN/m² (fetched from the
 *     public district-snapshot endpoint - a real, weekly-updated
 *     number, never invented) + three no-auth next steps: analyze a
 *     listing, browse live listings, open the district price map.
 *
 * All copy lives in i18n under blog.engage.*.
 */
import { useEffect, useState } from "react";
import { ArrowRight, Map as MapIcon, Search, List as ListIcon, TrendingUp } from "lucide-react";
import Link from "@/lib/localized-link";
import { useT } from "@/lib/locale-context";
import { fetchAPI } from "@/lib/api";
import TrendChart from "@/components/TrendChart";

interface DistrictRef {
  /** URL slug for /trends/<slug> (lib/districts.ts). */
  slug: string;
  /** Canonical AZ name - the aggregate key TrendChart queries by. */
  name: string;
}

interface SnapshotResponse {
  baku?: { median_ppm: number | null };
}

/** "3210" -> "3 210" (az/ru style thousands separator, matches
 *  MapSeoLanding's formatting). */
function fmtPpm(n: number): string {
  return Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

export default function BlogEngageBlock({ district }: { district?: DistrictRef | null }) {
  const t = useT();

  // Live Baku AZN/m² - public endpoint, cheap, and the number is real.
  // On any failure we simply omit the line (no invented numbers).
  const [bakuPpm, setBakuPpm] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchAPI<SnapshotResponse>("/trends/district-snapshot", { mode: "sale" })
      .then((res) => {
        if (!cancelled && res?.baku?.median_ppm) setBakuPpm(res.baku.median_ppm);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const steps = [
    {
      href: "/analyze",
      Icon: Search,
      title: t("blog.engage.analyze.title"),
      desc: t("blog.engage.analyze.desc"),
    },
    {
      href: "/listings",
      Icon: ListIcon,
      title: t("blog.engage.listings.title"),
      desc: t("blog.engage.listings.desc"),
    },
    {
      href: "/map/districts",
      Icon: MapIcon,
      title: t("blog.engage.map.title"),
      desc: t("blog.engage.map.desc"),
    },
  ];

  return (
    <aside className="mt-10">
      {/* District posts get the live trend chart for their district -
          the article's subject, moving, with real weekly numbers. */}
      {district && (
        <div className="mb-6">
          <h2 className="text-base font-bold text-gray-900">
            {t("blog.engage.trend.title").replace("{district}", district.name)}
          </h2>
          <div className="mt-3">
            <TrendChart level="district" locationKey={district.name} mode="sale" />
          </div>
          <Link
            href={`/trends/${district.slug}`}
            className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
          >
            {t("blog.engage.trend.link")} <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
          </Link>
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <h2 className="text-base font-bold text-gray-900">{t("blog.engage.title")}</h2>
        {bakuPpm != null && (() => {
          // Split on the placeholder so the live number itself can wear
          // tabular-nums + emphasis while the sentence stays plain.
          const [before, after] = t("blog.engage.baku_ppm").split("{ppm}");
          return (
            <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">
              <TrendingUp className="inline-block w-4 h-4 mr-1.5 -mt-0.5 text-gray-400" aria-hidden />
              {before}
              <span className="font-semibold tabular-nums text-gray-900">{fmtPpm(bakuPpm)}</span>
              {after}
            </p>
          );
        })()}
        <div className="mt-3 divide-y divide-gray-100">
          {steps.map(({ href, Icon, title, desc }) => (
            <Link
              key={href}
              href={href}
              className="group flex items-center gap-3 py-3 first:pt-1 last:pb-1"
            >
              <Icon className="w-4 h-4 shrink-0 text-gray-400 group-hover:text-gray-600 transition-colors" />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-gray-900 underline-offset-2 group-hover:underline">
                  {title}
                </span>
                <span className="block text-xs text-gray-500 leading-relaxed">{desc}</span>
              </span>
              <ArrowRight className="w-4 h-4 shrink-0 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-0.5 transition-[color,transform]" />
            </Link>
          ))}
        </div>
      </div>
    </aside>
  );
}
